/**
 * `InfoCardBindingModel` — the same shape goes out on `POST marketing/infocard`
 * and comes back from `GET marketing/infocards` (under `data.results`).
 *
 * Taken from `Gopanza.Api.Services.Abstractions/Models/Binding/Marketing`.
 */

/** `TargetGender`. */
export const TARGET_GENDER = { any: 0, male: 1, female: 2 } as const;

/**
 * `AgeGroup`. Note `all` is **4**, not 0 — the enum starts at `minor`, so an
 * omitted value targets minors rather than everybody. The editor always sends
 * one for that reason.
 */
export const AGE_GROUP = {
  minor: 0,
  teenager: 1,
  middleAged: 2,
  senior: 3,
  all: 4,
} as const;

export interface InfoCard {
  id: string | null;
  name: string | null;
  opacity: number;
  ordering: number;
  prioritize: boolean;
  /** The English asset. */
  imageUrl: string | null;
  /** The Spanish one, despite the name. */
  imageUrl2: string | null;
  titleEng: string | null;
  descriptionEng: string | null;
  titleSpa: string | null;
  descriptionSpa: string | null;
  bodySpa: string | null;
  bodyEng: string | null;
  /**
   * Second copies of the body, read by a different client. The API never
   * derives them, and the original editor wrote them from the same box as
   * `body*`; this one does the same rather than leaving them to drift.
   */
  bodySpa2: string | null;
  bodyEng2: string | null;
  htmlBodyEng: string | null;
  htmlBodySpa: string | null;
  actionEng: string | null;
  actionSpa: string | null;
  actionUrl: string | null;
  gender: number;
  ageRange: number;
  startDateTime: string | null;
  endDateTime: string | null;
  businessId: string | null;
  showInWeb: boolean;
  showInApp: boolean;
  useDefaultHeight: boolean;
  height: number;
  isActive: boolean;
}

export type CardStatus = 'live' | 'planned' | 'completed' | 'inactive';

/**
 * Where a card sits in its own schedule.
 *
 * `inactive` wins over the dates: the original derived status from the window
 * alone, so a card switched off still advertised itself as "Live" in the list
 * while showing nowhere.
 */
export function statusOf(card: InfoCard, now = Date.now()): CardStatus {
  if (!card.isActive) return 'inactive';

  const start = card.startDateTime ? Date.parse(`${card.startDateTime}Z`) : null;
  const end = card.endDateTime ? Date.parse(`${card.endDateTime}Z`) : null;

  if (end !== null && now > end) return 'completed';
  if (start !== null && now < start) return 'planned';
  return 'live';
}

/** Whole days left in a live card's window, or null when it never ends. */
export function daysRemaining(card: InfoCard, now = Date.now()): number | null {
  if (!card.endDateTime) return null;
  const end = Date.parse(`${card.endDateTime}Z`);
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - now) / 86_400_000));
}

/** A blank card, with the defaults the original seeded a new one with. */
export function emptyCard(): InfoCard {
  return {
    id: null,
    name: '',
    opacity: 0,
    ordering: 99,
    prioritize: false,
    imageUrl: '',
    imageUrl2: '',
    titleEng: '',
    descriptionEng: '',
    titleSpa: '',
    descriptionSpa: '',
    bodySpa: '',
    bodyEng: '',
    bodySpa2: '',
    bodyEng2: '',
    htmlBodyEng: '',
    htmlBodySpa: '',
    actionEng: '',
    actionSpa: '',
    actionUrl: '',
    gender: TARGET_GENDER.any,
    // The original left this unset, so every card it created was filed under
    // `minor` — the enum's zero — rather than under "all".
    ageRange: AGE_GROUP.all,
    startDateTime: null,
    endDateTime: null,
    businessId: null,
    showInWeb: false,
    showInApp: false,
    useDefaultHeight: true,
    height: 150,
    isActive: false,
  };
}
